/**
 * Help page: Automations — how rules work, in plain words.
 *
 * Route: /prioritization/documentation/automations (linked from the
 * Automations settings page and its builder, and from the Documentation hub).
 * Every behavior stated here is the engine's (migrations 817, 826, 832); the
 * words for triggers and actions come from src/lib/automationRules.ts so this
 * page and the builder say the same thing.
 */

import { Link } from 'react-router-dom';
import { Zap } from 'lucide-react';
import { DescriptionList } from '@/components/ds';
import { AreaDocLayout, DocDivider } from '../components/AreaDocLayout';
import { DocSection } from '../components/DocSection';
import { StepGuide } from '../components/StepGuide';
import { STARTER_RULES, describeRule } from '../../../../lib/automationRules';

const WHEN_ITEMS = [
  { label: 'an item changes to a state', value: 'It becomes deferred, archived or active again. You can name the state or take any change.' },
  { label: 'an item is created', value: 'A new item arrives, from any place items come from.' },
  {
    label: 'an item becomes ready for ranking',
    value:
      'The app finds the item has everything your workspace requires before ranking, such as a category, an altitude and any required attributes. The app checks this whenever the item is changed in the app. A change made through the API or the iOS app does not check it again, so it does not start the rule.',
    wide: true,
  },
  { label: 'an item is assigned to someone', value: 'The item gets an owner. You can name the person or take anyone.' },
  { label: 'an item moves to a queue', value: 'You can name the queue or take any queue.' },
  { label: 'an item is tagged', value: 'A tag the item did not have before. You can name the tag or take any tag.' },
  {
    label: 'an item has not changed for some days',
    value: 'An active item that nobody has changed for the number of days you set. The background jobs check this every 15 minutes, and a rule runs once for each quiet spell.',
    wide: true,
  },
  { label: 'a cycle moves to a phase', value: 'You can name the phase or take any phase.' },
  { label: 'a finding is raised', value: 'An agent proposes a finding for review. You can name the agent and how severe the finding must be.' },
  { label: 'someone accepts a finding', value: 'A person accepts a finding an agent raised.' },
];

const ONLY_IF_ITEMS = [
  { label: 'it is in a queue', value: 'The item sits in the queue you pick.' },
  { label: 'it is tagged', value: 'The item carries the tag you write. Capital letters do not matter.' },
  { label: 'its category is', value: 'The item has the category you pick. Capital letters do not matter.' },
  { label: 'it has no owner', value: 'Nobody is assigned to the item yet.' },
];

const THEN_ITEMS = [
  {
    label: 'notify',
    value:
      'The item’s owner, the workspace admins, people you pick, or everyone in a group. When the item has no owner, the admins hear instead. It arrives in the app and on phones that have the app. The person whose change started the rule is never told about their own change.',
    wide: true,
  },
  { label: 'assign it to', value: 'A person you pick, or the default owner of the item’s queue. Only when the item has no owner, unless you tick the box to replace one.' },
  { label: 'move it to', value: 'A queue you pick. If the item has no owner, the queue’s default owner becomes its owner, the same as moving it by hand.' },
  { label: 'tag it', value: 'Adds the tag, unless the item already has it.' },
  {
    label: 'change it to',
    value: 'Defers, archives or reactivates the item. It acts as the person who wrote the rule, and only while that person is still an admin of the workspace.',
    wide: true,
  },
  { label: 'run', value: 'Runs an agent on the next background run. What it finds comes to you for review; it changes nothing by itself.' },
  {
    label: 'send it to your webhooks',
    value: 'Tells another system, for a team that has connected one. A developer registers the receiving address with the Webhooks API and listens for the event automation.fired.',
    wide: true,
  },
];

export default function AutomationsDocs() {
  return (
    <AreaDocLayout
      icon={Zap}
      iconBg="bg-[var(--active-tint)] border-[var(--rule)]"
      iconColor="text-[var(--text-sec)]"
      title="Automations"
      subtitle="Rules that take the next step for you when something changes"
      tag="Admins"
    >
      <DocSection
        title="What a rule is"
        description="A rule is one sentence. When something happens, and only if a condition holds, do up to five things."
        accent="blue"
      >
        <ul className="space-y-2">
          {STARTER_RULES.map((d, i) => (
            <li key={i} className="font-serif text-[1.05rem] leading-snug text-[var(--text-pri)]">
              {describeRule(d)}
            </li>
          ))}
        </ul>
        <p className="mt-3 text-sm text-[var(--text-sec)]">Workspace admins write rules.</p>
      </DocSection>

      <DocSection title="Write your first rule" accent="none">
        <StepGuide
          color="blue"
          steps={[
            {
              title: 'Open Automations',
              description: 'Settings, then Workflows & Templates, then Automations.',
            },
            {
              title: 'Start from an example, or a blank rule',
              description: 'An example is a whole rule. Change any part of it before you save.',
            },
            {
              title: 'Fill in the blanks',
              description:
                'Each line reads as part of the sentence: When, Only if, Then. Use "and also" to add more things to do, up to five.',
            },
            {
              title: 'Try it on an item',
              description:
                'Pick any item. The page says what the rule would do to it, then undoes everything. Nothing is changed and no one is told.',
            },
            {
              title: 'Save',
              description: 'The rule runs from then on. Use the switch beside it to turn it off without losing it.',
              note: 'If something would stop the rule from working, the builder says so beside it, with the one thing to do.',
            },
          ]}
        />
      </DocSection>

      <DocDivider />

      <DocSection title="When: what can start a rule" accent="none">
        <DescriptionList items={WHEN_ITEMS} columns={2} divided />
      </DocSection>

      <DocSection title="Only if: conditions" description="Conditions apply to rules that start from an item. Add as many as you need; all of them must hold." accent="none">
        <DescriptionList items={ONLY_IF_ITEMS} columns={2} divided />
      </DocSection>

      <DocSection title="Then: what a rule can do" description="One rule can do up to five of these, in the order you list them." accent="none">
        <DescriptionList items={THEN_ITEMS} columns={2} divided />
      </DocSection>

      <DocDivider />

      <DocSection title="Agents in rules" accent="blue">
        <div className="space-y-2 text-sm text-[var(--text-sec)]">
          <p>An agent can start a rule, when it raises a finding, and a rule can run an agent.</p>
          <p>
            An agent never changes an item&rsquo;s state. When a rule that an agent started would defer, archive or
            reactivate an item, it asks the workspace admins and the item&rsquo;s owner to make the change instead.
          </p>
        </div>
      </DocSection>

      <DocSection title="Good to know" accent="none">
        <ul className="list-disc space-y-2 pl-5 text-sm text-[var(--text-sec)]">
          <li>
            Every time a rule runs, it shows under &ldquo;What the rules did&rdquo; on the Automations page, including
            the times it had nothing to do, and why.
          </li>
          <li>
            Rules on time and rules that send to webhooks run on the background jobs. If those are off, the builder says
            so and offers to switch them on.
          </li>
          <li>A rule&rsquo;s own change can start another rule. The chain stops after three steps.</li>
          <li>The same rule tells the same person about the same item once in ten minutes, however often it runs.</li>
          <li>Deleting a rule keeps what it has already done.</li>
        </ul>
      </DocSection>

      <p className="text-sm text-[var(--text-sec)]">
        <Link to="/prioritization/settings/automations" className="underline hover:text-[var(--text-pri)]">
          Go to Automations
        </Link>
      </p>
    </AreaDocLayout>
  );
}
